import {NewJoinerChecklistItemFilter} from "../../api/Model";
import React, {HTMLAttributes, ReactNode, useCallback} from "react";
import {Autocomplete, AutocompleteRenderOptionState, Box, Checkbox, Stack, TextField, Typography} from "@mui/material";
import {newJoinerChecklistItemFilterGroup} from "./NewJoinerApi";
import {NewJoinerChecklistItemFilterTag} from "./NewJoinerTypeTag";

type FilterOption = { value: NewJoinerChecklistItemFilter, label: ReactNode, description: string };
const FILTER_OPTIONS: FilterOption[] = [
    {
        value: NewJoinerChecklistItemFilter.Remote,
        label: 'Remote',
        description: 'Shown for the employees working remotely'
    },
    {
        value: NewJoinerChecklistItemFilter.Office,
        label: 'Office',
        description: 'Shown for the employees assigned to an office'
    },
    {
        value: NewJoinerChecklistItemFilter.NewFTE,
        label: 'New FTE',
        description: 'Shown for the new full-time employees'
    },
    {
        value: NewJoinerChecklistItemFilter.NewIntern,
        label: 'New Intern',
        description: 'Shown for the new interns'
    },
    {
        value: NewJoinerChecklistItemFilter.InternToFTE,
        label: 'Intern to FTE',
        description: 'Shown for the interns who are transitioning to full-time employees'
    },
    {
        value: NewJoinerChecklistItemFilter.ReturnFromParentalLeave,
        label: 'Return from Parental Leave',
        description: 'Shown for the employees returning from parental leave'
    },
    {
        value: NewJoinerChecklistItemFilter.InternationalRelocation,
        label: 'International Relocation',
        description: 'Shown for the employees relocating internationally'
    },
    {
        value: NewJoinerChecklistItemFilter.LocalRelocation,
        label: 'Local Relocation',
        description: 'Shown for the employees relocating locally'
    },
];

const groupTitle: Record<'work' | 'type', string> = {
    work: 'Work location filters',
    type: 'New joiner type filters',
}

const groupBy = (option: FilterOption) => groupTitle[newJoinerChecklistItemFilterGroup[option.value].group];

const renderOption = (
    optionProps: HTMLAttributes<HTMLLIElement> & { key: any },
    filterOption: FilterOption,
    {selected}: AutocompleteRenderOptionState,
) => {
    return (
        <Box {...optionProps} key={optionProps.key} component="li" sx={{width: '100%'}}>
            <Stack width="100%" direction="row" gap={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center">
                    <Checkbox checked={selected} style={{marginRight: 8}}/>
                    <NewJoinerChecklistItemFilterTag filter={filterOption.value}/>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {filterOption.description}
                </Typography>
            </Stack>
        </Box>
    );
};


export function ChecklistItemFilterSelect({filters, onChange}: {
    filters: NewJoinerChecklistItemFilter[],
    onChange: (filters: NewJoinerChecklistItemFilter[]) => void,
}) {
    const selectedFilters = FILTER_OPTIONS.filter(option => filters.includes(option.value));
    const handleChange = useCallback((_: React.SyntheticEvent, options: FilterOption[]) => {
        onChange(options.map(option => option.value));
    }, [onChange]);

    return (
        <Autocomplete
            multiple
            size="small"
            options={FILTER_OPTIONS}
            groupBy={groupBy}
            value={selectedFilters}
            onChange={handleChange}
            renderInput={params => <TextField {...params} label="Filters"/>}
            renderValue={(selected) => (
                <>{selected.map(filterOption => (
                    <NewJoinerChecklistItemFilterTag
                        key={filterOption.value}
                        filter={filterOption.value}
                        sx={{ml: 1}}
                        onDelete={() => onChange(filters.filter(f => f !== filterOption.value))}
                    />
                ))}</>
            )}

            renderOption={renderOption}
        />
    );
}